import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Switch,
  TextField,
} from "@mui/material";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";
import Field from "components/Field";
import FieldRichText from "components/FieldRichText";
import SelectApplication from "components/SelectApplication";
import SelectDismissalType from "components/SelectDismissalType";
import SelectDisplayMode from "components/SelectDisplayMode";
import SelectFrequencyType from "components/SelectFrequencyType";
import SelectIntervalType from "components/SelectIntervalType";
import SelectNotificationChannel from "components/SelectNotificationChannel";
import SelectNotificationTrigger from "components/SelectNotificationTrigger";
import { FrequencyTypeIds } from "constants/FrequencyTypeConstants";
import { NotificationChannelIds } from "constants/NotificationChannelConstants";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { useCallback, useMemo } from "react";
import { Form } from "react-final-form";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  EmailThemeRequest,
  INotificationConfigurationRequest,
  NotificationConfigurationModel,
  NotificationConfigurationRequest,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { convertToUTC, convertToZonedDateTimeLocal } from "utils/dateUtils";
import translateError from "utils/translateError";
import InAppPreview from "./InAppPreview";

export enum NotificationConfigFormMode {
  Create = "create",
  Update = "update",
}

type NotificationConfigFormValues = Omit<
  INotificationConfigurationRequest,
  "validFrom" | "expiresOn" | "body"
> & {
  validFrom?: string;
  expiresOn?: string;
  condition?: string;
  channel?: string;
  emailBody?: string;
  inAppBody?: string;
};

type NotificationConfigurationFormProps = {
  configuration?: NotificationConfigurationModel;
  mode: NotificationConfigFormMode;
};

const NotificationConfigurationForm = ({
  configuration,
  mode,
}: NotificationConfigurationFormProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatMessage: t } = useIntl();
  const { organizationId } = contextStore.useState();
  const {
    notificationsConfigurationsService,
    notificationConfigurationDetailsUrl,
    notificationConfigurationsUrl,
    notificationService,
  } = useGlobalContext();

  const isUpdate = mode === NotificationConfigFormMode.Update;

  const initialValues = useMemo<NotificationConfigFormValues>(() => {
    if (configuration) {
      return {
        ...configuration,
        isEnabled: configuration.isEnabled ?? false,
        notificationRoutingTypeId: configuration.routingTypeId,
        title: configuration.templateTitle,
        emailBody:
          configuration.routingTypeId?.toLowerCase() ===
          NotificationChannelIds.Email
            ? configuration.body
            : undefined,
        inAppBody:
          configuration.routingTypeId?.toLowerCase() ===
          NotificationChannelIds.InApp
            ? configuration.body
            : undefined,
        validFrom: configuration.validFrom
          ? convertToZonedDateTimeLocal(
              new Date(configuration.validFrom.toString()),
            )
          : undefined,
        expiresOn: configuration.expiresOn
          ? convertToZonedDateTimeLocal(
              new Date(configuration.expiresOn.toString()),
            )
          : undefined,
      };
    }
    return {
      isEnabled: true,
      organizationId: organizationId,
    };
  }, [configuration, organizationId]);

  const themeQuery = useQuery(
    ["notificationService.getEmailThemeByOrganization", organizationId],
    () =>
      notificationService.getEmailThemeByOrganization(
        EmailThemeRequest.fromJS({
          organizationId,
        }),
      ),
    {
      enabled: !!organizationId,
    },
  );

  const onSubmit = useCallback(
    async (values: NotificationConfigFormValues) => {
      try {
        const request = new NotificationConfigurationRequest({
          ...values,
          applicationId: values.applicationId,
          organizationId: values.organizationId || organizationId,
          body:
            values.notificationRoutingTypeId?.toLowerCase() ===
            NotificationChannelIds.Email
              ? values.emailBody
              : values.notificationRoutingTypeId?.toLowerCase() ===
                NotificationChannelIds.InApp
              ? values.inAppBody
              : undefined,
          validFrom: convertToUTC(
            values.validFrom ? new Date(values.validFrom) : undefined,
          ),
          expiresOn: convertToUTC(
            values.expiresOn ? new Date(values.expiresOn) : undefined,
          ),
        });

        const response = await notificationsConfigurationsService.saveNotificationConfiguration(
          request,
        );

        queryClient.invalidateQueries([
          "notificationsConfigurationsService.getNotificationsConfigurations",
        ]);
        queryClient.invalidateQueries([
          "notificationsConfigurationsService.getNotificationConfiguration",
        ]);

        if (response?.id) {
          navigate(notificationConfigurationDetailsUrl(response.id));
        } else if (values.notificationJobRoutingId) {
          navigate(
            notificationConfigurationDetailsUrl(
              values.notificationJobRoutingId,
            ),
          );
        } else {
          navigate(notificationConfigurationsUrl());
        }
      } catch (err) {
        return { [FORM_ERROR]: translateError(err, t) };
      }
    },
    [
      organizationId,
      notificationsConfigurationsService,
      queryClient,
      navigate,
      notificationConfigurationDetailsUrl,
      notificationConfigurationsUrl,
      t,
    ],
  );

  const submitLabel = isUpdate
    ? t({ id: "updateNotificationConfiguration" })
    : t({ id: "createNotificationConfiguration" });

  return (
    <Form<NotificationConfigFormValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({
        form: { change },
        handleSubmit,
        submitting,
        submitError,
        values,
      }) => {
        const selectedChannelId = values.notificationRoutingTypeId?.toLowerCase();
        const isEmail = selectedChannelId === NotificationChannelIds.Email;
        const isInApp = selectedChannelId === NotificationChannelIds.InApp;
        const showTimeInterval =
          values.frequencyTypeId === FrequencyTypeIds.RepeatEvery;

        return (
          <form onSubmit={handleSubmit} noValidate>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Field name="isEnabled" type="checkbox">
                  {({ input }) => (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!input.checked}
                            onChange={input.onChange}
                          />
                        }
                        label={
                          input.checked ? t({ id: "sent" }) : t({ id: "draft" })
                        }
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="notificationJobEventId" required>
                  {({ input, meta }) => (
                    <Grid item xs={12} md={6}>
                      <SelectNotificationTrigger
                        value={input.value || undefined}
                        onChange={(id) => input.onChange(id)}
                        label={t({ id: "trigger" })}
                        disabled={isUpdate}
                        error={meta.error && meta.submitFailed}
                        required
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="notificationRoutingTypeId" required>
                  {({ input, meta }) => (
                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        error={!isUpdate && meta.error && meta.submitFailed}
                      >
                        <SelectNotificationChannel
                          multiple={false}
                          value={input.value ?? ""}
                          onChange={input.onChange}
                          label={t({ id: "channel" })}
                          disabled={isUpdate}
                          textFieldProps={{
                            variant: "standard",
                            error: !isUpdate && meta.error && meta.submitFailed,
                          }}
                          required
                        />
                        {!isUpdate && meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Field name="condition">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "condition" })}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <ContextOrganizationField
                  values={values}
                  formChange={change}
                  mode={
                    isUpdate
                      ? ContextOrganizationFieldMode.EditDisabled
                      : ContextOrganizationFieldMode.SetByHeader
                  }
                />

                <Field name="applicationId" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectApplication
                          value={input.value || undefined}
                          onChange={input.onChange}
                          organizationId={values.organizationId}
                          label={t({ id: "applicationLabel" })}
                          disabled={isUpdate}
                          textFieldProps={{
                            variant: "standard",
                            error: meta.error && meta.submitFailed,
                            required: true,
                          }}
                          required
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                {isEmail && (
                  <Grid item xs={12}>
                    <TextField
                      disabled
                      fullWidth
                      required
                      value={
                        themeQuery?.data?.data?.inheritedOrganizationName ?? ""
                      }
                      variant="standard"
                      label={t({ id: "emailTheme" })}
                    />
                  </Grid>
                )}

                <Field name="templateName" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "templateName" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                {isEmail && (
                  <>
                    <Field name="subject" required>
                      {({ input, meta }) => (
                        <Grid item xs={12}>
                          <TextField
                            variant="standard"
                            fullWidth
                            label={t({ id: "subject" })}
                            InputLabelProps={{ required: true }}
                            error={meta.error && meta.submitFailed}
                            helperText={
                              meta.error && meta.submitFailed && meta.error
                            }
                            {...input}
                          />
                        </Grid>
                      )}
                    </Field>

                    <Field name="senderName" required>
                      {({ input, meta }) => (
                        <Grid item xs={12} md={6}>
                          <TextField
                            variant="standard"
                            fullWidth
                            label={t({ id: "senderName" })}
                            InputLabelProps={{ required: true }}
                            error={meta.error && meta.submitFailed}
                            helperText={
                              meta.error && meta.submitFailed && meta.error
                            }
                            {...input}
                          />
                        </Grid>
                      )}
                    </Field>

                    <Field name="senderEmail" required>
                      {({ input, meta }) => (
                        <Grid item xs={12} md={6}>
                          <TextField
                            variant="standard"
                            fullWidth
                            label={t({ id: "senderEmail" })}
                            InputLabelProps={{ required: true }}
                            error={meta.error && meta.submitFailed}
                            helperText={
                              meta.error && meta.submitFailed && meta.error
                            }
                            {...input}
                          />
                        </Grid>
                      )}
                    </Field>

                    <Field name="ccEmails">
                      {({ input, meta }) => (
                        <Grid item xs={12} md={6}>
                          <TextField
                            variant="standard"
                            fullWidth
                            label={t({ id: "ccEmails" })}
                            error={meta.error && meta.submitFailed}
                            helperText={
                              meta.error && meta.submitFailed && meta.error
                            }
                            {...input}
                          />
                        </Grid>
                      )}
                    </Field>

                    <Field name="bccEmails">
                      {({ input, meta }) => (
                        <Grid item xs={12} md={6}>
                          <TextField
                            variant="standard"
                            fullWidth
                            label={t({ id: "bccEmails" })}
                            error={meta.error && meta.submitFailed}
                            helperText={
                              meta.error && meta.submitFailed && meta.error
                            }
                            {...input}
                          />
                        </Grid>
                      )}
                    </Field>

                    <Field name="emailBody">
                      {({ input, meta }) => (
                        <Grid item xs={12}>
                          <FieldRichText
                            input={input}
                            meta={meta}
                            label={t({ id: "notificationBody" })}
                            cleanupClasses={false}
                            init={{
                              height: 500,
                            }}
                          />
                        </Grid>
                      )}
                    </Field>
                  </>
                )}

                {isInApp && (
                  <>
                    <Grid item xs={12} md={7}>
                      <Grid container spacing={2}>
                        <Field name="displayModeId" required>
                          {({ input, meta }) => (
                            <Grid item xs={12}>
                              <SelectDisplayMode
                                value={input.value || undefined}
                                onChange={(id) => input.onChange(id)}
                                label={t({ id: "displayMode" })}
                                disabled={isUpdate}
                                error={meta.error && meta.submitFailed}
                                required
                              />
                            </Grid>
                          )}
                        </Field>

                        <Field name="title" required>
                          {({ input, meta }) => (
                            <Grid item xs={12}>
                              <TextField
                                variant="standard"
                                fullWidth
                                label={t({ id: "titleSubject" })}
                                InputLabelProps={{ required: true }}
                                error={meta.error && meta.submitFailed}
                                helperText={
                                  meta.error && meta.submitFailed && meta.error
                                }
                                {...input}
                              />
                            </Grid>
                          )}
                        </Field>

                        <Field name="inAppBody" required>
                          {({ input, meta }) => (
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                variant="outlined"
                                label={t({ id: "notificationBody" })}
                                InputLabelProps={{ required: true }}
                                multiline
                                minRows={3}
                                maxRows={6}
                                inputProps={{ maxLength: 500 }}
                                error={meta.touched && !!meta.error}
                                helperText={
                                  meta.touched && meta.error
                                    ? meta.error
                                    : `${(input.value || "").length}/500`
                                }
                                {...input}
                              />
                            </Grid>
                          )}
                        </Field>

                        <Field name="dismissalTypeId">
                          {({ input }) => (
                            <Grid item xs={12} md={6}>
                              <SelectDismissalType
                                value={input.value || undefined}
                                onChange={(id) => input.onChange(id)}
                                label={t({ id: "dismissal" })}
                              />
                            </Grid>
                          )}
                        </Field>

                        <Field name="frequencyTypeId" required>
                          {({ input }) => (
                            <Grid item xs={12} md={6}>
                              <SelectFrequencyType
                                value={input.value || undefined}
                                onChange={(id) => input.onChange(id)}
                                label={t({ id: "frequency" })}
                                required
                              />
                            </Grid>
                          )}
                        </Field>

                        {showTimeInterval && (
                          <>
                            <Field name="frequencyInterval">
                              {({ input, meta }) => (
                                <Grid item xs={6}>
                                  <TextField
                                    variant="standard"
                                    fullWidth
                                    label={t({ id: "frequencyInterval" })}
                                    type="number"
                                    error={meta.error && meta.submitFailed}
                                    helperText={
                                      meta.error &&
                                      meta.submitFailed &&
                                      meta.error
                                    }
                                    {...input}
                                  />
                                </Grid>
                              )}
                            </Field>

                            <Field name="intervalTypeId">
                              {({ input }) => (
                                <Grid item xs={6}>
                                  <SelectIntervalType
                                    value={input.value || undefined}
                                    onChange={(id) => input.onChange(id)}
                                    label={t({ id: "intervalType" })}
                                  />
                                </Grid>
                              )}
                            </Field>
                          </>
                        )}

                        <Field name="actionUrl">
                          {({ input, meta }) => (
                            <Grid item xs={12} md={6}>
                              <TextField
                                variant="standard"
                                fullWidth
                                label={t({ id: "actionUrl" })}
                                error={meta.error && meta.submitFailed}
                                helperText={
                                  meta.error && meta.submitFailed && meta.error
                                }
                                {...input}
                              />
                            </Grid>
                          )}
                        </Field>

                        <Field name="actionLabel">
                          {({ input, meta }) => (
                            <Grid item xs={12} md={6}>
                              <TextField
                                variant="standard"
                                fullWidth
                                label={t({ id: "actionLabel" })}
                                error={meta.error && meta.submitFailed}
                                helperText={
                                  meta.error && meta.submitFailed && meta.error
                                }
                                {...input}
                              />
                            </Grid>
                          )}
                        </Field>

                        <Field name="validFrom">
                          {({ input, meta }) => (
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                variant="standard"
                                label={t({ id: "validFrom" })}
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                error={meta.error && meta.submitFailed}
                                helperText={
                                  meta.error && meta.submitFailed && meta.error
                                }
                                {...input}
                                value={input.value || ""}
                              />
                            </Grid>
                          )}
                        </Field>

                        <Field name="expiresOn">
                          {({ input, meta }) => (
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                variant="standard"
                                label={t({ id: "expiresOn" })}
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                error={meta.error && meta.submitFailed}
                                helperText={
                                  meta.error && meta.submitFailed && meta.error
                                }
                                {...input}
                                value={input.value || ""}
                              />
                            </Grid>
                          )}
                        </Field>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <Box sx={{ position: "sticky", top: 24 }}>
                        <InAppPreview
                          displayModeId={values.displayModeId}
                          title={values.title}
                          body={values.inAppBody}
                          actionUrl={values.actionUrl}
                          actionLabel={values.actionLabel}
                        />
                      </Box>
                    </Grid>
                  </>
                )}

                {submitError && (
                  <Grid item xs={12}>
                    <Alert severity="error">{submitError}</Alert>
                  </Grid>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() => {
                  if (isUpdate && configuration?.notificationJobRoutingId) {
                    navigate(
                      notificationConfigurationDetailsUrl(
                        configuration.notificationJobRoutingId,
                      ),
                    );
                  } else {
                    navigate(notificationConfigurationsUrl());
                  }
                }}
              >
                {t({ id: "cancelButton" })}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={submitting}
              >
                {submitLabel}
              </Button>
            </DialogActions>
          </form>
        );
      }}
    />
  );
};

export default NotificationConfigurationForm;
